import { AccountStatuses, BreadAccount, BreadAccountType } from "breadcommon";

/** Converts an amount in `currency` to the user's default currency. */
type ConvertToDefault = (amount: number, currency: string) => number;

export type BalanceGroup = {
  id: string;
  label: string;
  accounts: BreadAccount[];
  /** In the user's default currency. */
  total: number;
};

/** Each account type's group, in display order. */
const GROUPS: { type: BreadAccountType; label: string }[] = [
  { type: "credit", label: "Credit cards" },
  { type: "depository", label: "Cash" },
  { type: "investment", label: "Investments" },
  { type: "loan", label: "Loans" },
  { type: "other", label: "Other" },
];

/** Accounts that count toward net worth: linked and not closed. */
function activeAccounts(accounts: Map<string, BreadAccount>): BreadAccount[] {
  return Array.from(accounts.values()).filter(
    (account) => account.status === AccountStatuses.ACTIVE,
  );
}

function sumBalances(
  accounts: BreadAccount[],
  convertToDefault: ConvertToDefault,
): number {
  return accounts.reduce(
    (sum, account) =>
      account.current_balance
        ? sum + convertToDefault(account.current_balance, account.currency_code)
        : sum,
    0,
  );
}

/**
 * Net worth across active accounts, in the default currency. Amounts owed are
 * already negative (see parseBreadAccount), so this is a plain sum.
 */
export function computeNetWorth(
  accounts: Map<string, BreadAccount>,
  convertToDefault: ConvertToDefault,
): number {
  return sumBalances(activeAccounts(accounts), convertToDefault);
}

/** Active accounts grouped by kind, in display order, skipping empty groups. */
export function groupAccountBalances(
  accounts: Map<string, BreadAccount>,
  convertToDefault: ConvertToDefault,
): BalanceGroup[] {
  const sorted = activeAccounts(accounts).sort(
    (a, b) => a.order_position - b.order_position,
  );
  return GROUPS.map((group) => ({
    id: group.type,
    label: group.label,
    accounts: sorted.filter((account) => account.type === group.type),
  }))
    .filter((group) => group.accounts.length > 0)
    .map((group) => ({
      ...group,
      total: sumBalances(group.accounts, convertToDefault),
    }));
}

// Plaid subtypes are lowercase ("cd", "money market", "roth 401k").
const UPPERCASE_WORDS = new Set([
  "cd",
  "ebt",
  "fsa",
  "gic",
  "hra",
  "hsa",
  "ira",
  "isa",
  "lif",
  "lira",
  "prif",
  "rdsp",
  "resp",
  "rlif",
  "rrif",
  "rrsp",
  "sep",
  "sipp",
  "tfsa",
  "ugma",
  "utma",
]);
const RETIREMENT_PLAN = /^(401|403|457)([a-z])$/;

/** A Plaid subtype in sentence case, e.g. "CD", "Money market", "Roth 401(k)". */
export function formatAccountSubtype(subtype: string): string {
  const words = subtype.split(" ").map((word) => {
    if (UPPERCASE_WORDS.has(word)) return word.toUpperCase();
    if (word === "roth") return "Roth";
    const plan = word.match(RETIREMENT_PLAN);
    return plan ? `${plan[1]}(${plan[2]})` : word;
  });
  const sentence = words.join(" ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
